"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.renderStudioPlayer = void 0;
var types_1 = require("./types");
var postmessage_interface_1 = __importDefault(require("@screencloud/postmessage-interface"));
var utils_1 = require("./utils");
var debounce_1 = __importDefault(require("lodash/debounce"));
var isEqual_1 = __importDefault(require("lodash/isEqual"));
exports.renderStudioPlayer = function (options) {
    var _a;
    var exposedInterface = null;
    var previousOverrideAppInitialize = options.overrideAppInitialize || {};
    var frame = undefined;
    var resizeWindowEventListener;
    //Todo: add function interface
    var configUpdateAvailableCallback = undefined;
    var requestFileCallback = undefined;
    var requestAuthTokenCallback = undefined;
    var sidebarPreviewItemCallback = undefined;
    var activeItemChangeCallback;
    var contentListReportUpdateCallback;
    var listenerCounter = new utils_1.ListenerCounter();
    function addListener(type, listener) {
        listenerCounter.add(type, listener);
    }
    function removeListener(type, listener) {
        listenerCounter.remove(type, listener);
    }
    var target = options.target, features = options.features, playerUrl = options.playerUrl, studioPlayerOptions = __rest(options, ["target", "features", "playerUrl"]);
    var token = studioPlayerOptions.token, region = studioPlayerOptions.region, contentPath = studioPlayerOptions.contentPath;
    //Parent can simulate screen size
    var simulatedDimensions = features === null || features === void 0 ? void 0 : features.simulatedDimensions;
    var debounceResizeDuration = 200;
    // Promise to return when requestConfigUpdate() is called by studio.
    // exposedInterface.emit will receive the config from the player and resolve the promise
    var requestConfigUpdatePromise = undefined;
    var resolveRequestConfigUpdatePromise = undefined;
    //Provide handler to parent to handle update from studio player
    var on = function (type, callback) {
        switch (type) {
            case "configUpdateAvailable":
                configUpdateAvailableCallback = callback;
                break;
            case "requestFiles":
                requestFileCallback = callback;
                break;
            case "requestAuthToken":
                requestAuthTokenCallback = callback;
                break;
            case types_1.ActiveItemChangeMessageType:
                activeItemChangeCallback = callback;
                break;
            case types_1.ContentListReportUpdateMessageType:
                contentListReportUpdateCallback = callback;
            case types_1.SidebarPreviewItemType:
                sidebarPreviewItemCallback = callback;
                break;
            default:
                throw new Error("Can't register event callback. Unknown event type: " + type + ".");
        }
    };
    var requestConfigUpdate = function (options) {
        // clear the promise each time to ensure we respond to the latest call
        requestConfigUpdatePromise = new Promise(function (resolve) {
            resolveRequestConfigUpdatePromise = resolve;
        });
        listenerCounter
            .get("SP_REQUEST_CONFIG_UPDATE")
            .forEach(function (listener) { return listener({ data: options }); });
        return requestConfigUpdatePromise;
    };
    var createPlaybackControlInterface = function () {
        var skipForward = function (zoneId) {
            var listeners = listenerCounter.get(types_1.SkipForwardMessageType);
            listeners.forEach(function (listener) { return listener({ zoneId: zoneId }); });
        };
        var skipBack = function (zoneId) {
            listenerCounter
                .get(types_1.SkipBackMessageType)
                .forEach(function (listener) { return listener({ zoneId: zoneId }); });
        };
        var play = function () {
            listenerCounter.get(types_1.PlayMessageType).forEach(function (listener) { return listener(); });
        };
        var pause = function () {
            listenerCounter.get(types_1.PauseMessageType).forEach(function (listener) { return listener(); });
        };
        var previewItem = function (contentId, zoneId) {
            listenerCounter
                .get(types_1.SidebarPreviewItemType)
                .forEach(function (listener) { return listener({ contentId: contentId, zoneId: zoneId }); });
        };
        return {
            skipForward: skipForward,
            skipBack: skipBack,
            play: play,
            pause: pause,
            previewItem: previewItem,
        };
    };
    // Check for mandatory props.
    if (!target || !token || !region || !contentPath) {
        // todo: why not throwing an error?
        console.error("[Studio Player SDK] Missing required parameters. Stopping.");
        return null;
    }
    if (!target.getElementsByTagName("iframe").length && !frame) {
        // Apply defaults
        var environment = options.environment || "production";
        var targetWidth = target.clientWidth;
        var targetHeight = target.clientHeight;
        //create iframe
        frame = utils_1.createFrame(environment, playerUrl, features === null || features === void 0 ? void 0 : features.simulatedDimensions, targetWidth, targetHeight);
        // Track resizing of window to redraw the iframe
        if (features === null || features === void 0 ? void 0 : features.simulatedDimensions) {
            resizeWindowEventListener = debounce_1.default(function () {
                var targetWidth = target.clientWidth;
                var targetHeight = target.clientHeight;
                utils_1.applyScaleStyling(frame, simulatedDimensions, targetWidth, targetHeight);
            }, debounceResizeDuration);
            window.addEventListener("resize", resizeWindowEventListener);
        }
        // Expose interface to receive message from player
        exposedInterface = new postmessage_interface_1.default.ExposedInterface({
            getSPInitialiseData: function () {
                //Initialise with studio player data
                return __assign(__assign({}, studioPlayerOptions), { features: features });
            },
            //Bubble up any messages passed from studio player to the parent
            emit: function (type, interfaceData) {
                var requestId = interfaceData.requestId;
                var data = interfaceData.payload;
                if (type === "configUpdateAvailable") {
                    if (configUpdateAvailableCallback) {
                        configUpdateAvailableCallback();
                    }
                }
                if (type === "requestConfigUpdate") {
                    // promise is set by studio calling requestConfigUpdate()
                    resolveRequestConfigUpdatePromise === null || resolveRequestConfigUpdatePromise === void 0 ? void 0 : resolveRequestConfigUpdatePromise(data);
                }
                if (type === "requestFiles") {
                    if (requestFileCallback) {
                        requestFileCallback(data).then(function (mediaData) {
                            listenerCounter
                                .get("SP_SEND_REQUESTED_FILES")
                                .forEach(function (listener) {
                                return listener({ data: mediaData, requestId: requestId });
                            });
                        });
                    }
                }
                if (type === "requestAuthToken") {
                    if (requestAuthTokenCallback) {
                        requestAuthTokenCallback(data).then(function (authData) {
                            listenerCounter
                                .get("SP_SEND_REQUEST_AUTH_TOKEN")
                                .forEach(function (listener) {
                                return listener({ data: authData, requestId: requestId });
                            });
                        });
                    }
                }
                if (type === types_1.ActiveItemChangeMessageType) {
                    if (activeItemChangeCallback) {
                        var messagePayload = data;
                        activeItemChangeCallback(messagePayload.activeItem, messagePayload.zoneId);
                    }
                }
                if (type === types_1.ContentListReportUpdateMessageType) {
                    if (contentListReportUpdateCallback) {
                        var report = data.report;
                        contentListReportUpdateCallback(report);
                    }
                }
                if (type === types_1.SidebarPreviewItemType) {
                    var messagePayload = data;
                    if (sidebarPreviewItemCallback) {
                        sidebarPreviewItemCallback(messagePayload.contentId, messagePayload.zoneId);
                    }
                }
            },
            // Allow events to be registered from Remote Interface (i.e The player)
            addListener: addListener,
            removeListener: removeListener,
        }, {
            guard: function (event) {
                if (process.env.NODE_ENV === "test") {
                    // ignore guard in the test environment to allow easy mocks
                    return true;
                }
                return event.source === (frame === null || frame === void 0 ? void 0 : frame.contentWindow);
            },
        });
        target.appendChild(frame);
        process.env.NODE_ENV !== "production" &&
            console.log("[Studio Player SDK] Player iframe created.");
    }
    return {
        setOverrideAppInitialize: function (nextOverrideAppInitialize) {
            if (!isEqual_1.default(previousOverrideAppInitialize, nextOverrideAppInitialize)) {
                listenerCounter
                    .get("SP_UPDATE_OVERRIDE_APP_INITIALIZE")
                    .forEach(function (listener) { return listener(nextOverrideAppInitialize); });
                previousOverrideAppInitialize = nextOverrideAppInitialize;
            }
        },
        on: on,
        requestConfigUpdate: requestConfigUpdate,
        resetPlayerScaling: function () {
            var targetWidth = target.clientWidth;
            var targetHeight = target.clientHeight;
            utils_1.applyScaleStyling(frame, simulatedDimensions, targetWidth, targetHeight);
        },
        destroy: function () {
            frame === null || frame === void 0 ? void 0 : frame.remove();
            exposedInterface === null || exposedInterface === void 0 ? void 0 : exposedInterface.dispose();
            listenerCounter.clear();
            // Clean up event listener
            if (resizeWindowEventListener) {
                window.removeEventListener("resize", resizeWindowEventListener);
            }
        },
        controls: ((_a = options.features) === null || _a === void 0 ? void 0 : _a.enablePlaybackControlInterface) ? createPlaybackControlInterface()
            : undefined,
    };
};
